/* AS-01 · HELP HOME — real tier, detector-driven.
   « les questions du moment, pour vous » is not a static FAQ: its first row
   reads the same supports fixture that feeds « À traiter » on Accueil, so the
   silent support and its day count are the world’s own story.

   Batch 9 (06/09): Aide opens on the SIX steps of the guide — `GUIDE_STEPS`
   in @/lib/vocab, the same six « Démarrer » walks, in the same order and the
   same words — with the seven-word vocabulary card after them (C-01 · C-04).
   The seven-step detail (`SETUP_STEPS`) is retired: one setup (setup spec MA-1,
   08/10). One copy of the words: this screen states nothing of its own. */
import { useNavigate } from 'react-router-dom'
import { Button, Card, EmptyState, ListRow, Input, List, PageHeader, Pill, Rule, Skeleton } from '@/ui'
import { GUIDE_STEPS, VOCAB } from '@/lib/vocab'
import { useHelpTopics } from '@/routes/parametres/api'

/* v3.3 WF-046 (pack tests-aide) · six task questions, each with its answer on the page and the door to the page that does it —
   « Reprendre la bonne étape ». The words are the reference's where they fit the app: the till's lock is the app's (five tries,
   a lock a responsable can lift — routes/till), the print line names the app's file (a PDF, printed at its real size), and
   « Un clic est-il un avis ? » restates the guardrail: no proof of a review is ever asked. Figures the app has no source for
   (the reference's « 15 minutes », « A6 at 100 % ») are not written here. */
const TASKS: { q: string; a: string; to: string }[] = [
  { q: 'Trouver ma fiche Google', a: 'Dans Google Maps, ouvrez la fiche de votre établissement, puis Partager et Copier le lien. Vérifiez le nom et l’adresse avant de l’utiliser.', to: '/parametres/plateformes' },
  { q: 'Ma campagne ne se publie pas', a: 'Utilisez « Vérifier » à l’étape 3 : les points à régler indiquent le champ ou la bibliothèque à corriger. Votre brouillon reste conservé.', to: '/campagnes' },
  { q: 'Imprimer mon chevalet', a: 'Ouvrez le support, téléchargez son PDF et imprimez-le à sa taille réelle. Vérifiez le scan depuis son emplacement.', to: '/supports' },
  { q: 'Un client ne retrouve pas son cadeau', a: 'Aidez-le à rouvrir son e-mail cadeau ou Mes cadeaux. Ne créez pas un second cadeau pour remplacer un simple problème d’accès.', to: '/clients?vue=cadeaux' },
  { q: 'La caisse est verrouillée', a: 'Après cinq codes incorrects, le code personnel est verrouillé un moment. Un autre membre peut se connecter normalement ; un responsable peut déverrouiller.', to: '/equipe' },
  { q: 'Un clic est-il un avis ?', a: 'Non. Build my reviews observe l’ouverture du lien. Toutes les notes ouvrent la même destination ; aucune preuve de publication n’est demandée. Le cadeau reste disponible, avec ou sans avis.', to: '/aide/article' },
]

export default function AS01Aide() {
  const { data, isPending, isError, refetch } = useHelpTopics()
  const nav = useNavigate()

  if (isPending) return (
    <div style={{ display: 'grid', gap: 10 }}>
      <Skeleton width="42%" /><Skeleton /><Skeleton width="65%" />
    </div>
  )
  if (isError) return (
    <EmptyState title="Impossible de charger l’aide."
      why="Votre connexion a peut-être sauté — rien n’a été perdu."
      action={<Button variant="primary" size="sm" onClick={() => refetch()}>Réessayer</Button>} />
  )

  return (
    <section data-frame="AS-01" style={{ display: 'grid', gap: 14 }}>
      <PageHeader title="Aide et support"
        aside={<span className="t-muted" style={{ fontSize: 12 }}>{data.crumb}</span>}
        description="Ce qu’il faut avoir fait, dans l’ordre, et ce que chaque mot veut dire ici." />

      {/* ---------- the six steps of the guide and the seven words: the whole batch-9
          addition to the v5 frame, declared as one region so diffcheck compares the
          rest of AS-01 exactly (C-01 · C-04) ---------- */}
      <div data-delta="batch 9 ① — les six étapes, les sept mots"
        style={{ display: 'grid', gap: 14 }}>
      <Rule>votre installation, en six étapes</Rule>
      <List>
        {GUIDE_STEPS.map((s, i) => (
          <ListRow key={s.short}
            icon={<span className="esect-n" aria-hidden>{i + 1}</span>}
            title={s.title} meta={s.line}
            end={<Button size="sm" variant="quiet" onClick={() => nav(`/guide?etape=${i + 1}`)}>Ouvrir</Button>} />
        ))}
      </List>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button variant="primary" size="sm" onClick={() => nav('/guide?etape=1')}>Démarrer</Button>
        <span className="f-help" style={{ alignSelf: 'center' }}>
          les six étapes une par une — vous pouvez vous arrêter et reprendre.
        </span>
      </div>

      {/* ---------- the seven words ---------- */}
      <Rule>les sept mots de Build my reviews</Rule>
      <List>
        {Object.values(VOCAB).map(v => (
          <ListRow key={v.word} title={v.word} meta={v.sentence} />
        ))}
      </List>

      </div>

      {/* ---------- v3.3 WF-046 · « Reprendre la bonne étape » — the six task questions, answered here, with their door ---------- */}
      <div data-delta="migration v3.3 WF-046 — six questions, their answers, their doors" data-testid="reprendre" style={{ display: 'grid', gap: 14 }}>
        <Rule>reprendre la bonne étape</Rule>
        <p className="f-help" style={{ margin: 0 }}>Des réponses courtes, reliées à votre tâche.</p>
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))' }}>
          {TASKS.map(t => (
            <Card key={t.q} data-testid="reprendre-question">
              <p className="bld-t" style={{ margin: '0 0 6px', fontSize: 15 }}>{t.q}</p>
              <p className="t-body" style={{ margin: '0 0 10px', fontSize: 13 }}>{t.a}</p>
              <Button size="sm" variant="quiet" onClick={() => nav(t.to)}>Ouvrir la bonne page</Button>
            </Card>
          ))}
        </div>
      </div>

      <Rule>les questions du moment, pour vous</Rule>
      <Input placeholder="Décrivez votre problème en quelques mots…"
        aria-label="Décrivez votre problème" readOnly />
      <List>
        {data.questions.map((q, i) => (
          <ListRow key={q.title} title={q.title} meta={q.meta}
            end={i === 0 && data.questions.length === 4
              ? <Pill tone="warn">Lié à votre compte</Pill>
              : undefined} />
        ))}
      </List>
      <Rule>autrement</Rule>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button variant="primary" size="sm" onClick={() => nav('/aide/contact')}>Écrire au support</Button>
        <Button variant="quiet" size="sm" onClick={() => nav('/aide/article')}>Guides par thème</Button>
        <Button variant="quiet" size="sm">État du service</Button>
      </div>
    </section>
  )
}
